import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {NotificationManager} from 'react-notifications';
import {API_BASE, API_ENDPOINT} from '../config';
import {useAuth} from '../auth/AuthContext';

const formatBytes = (b) => {
    if (!b) return '—';
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('ro-RO') : '—');

// Caile vin gata construite de server (providerul Sonata), ca in /me - aici doar
// prefixam cu API_BASE. Afisam thumb-ul in grila si legam la original.
const mediaUrl = (path) => (path ? `${API_BASE}${path}` : null);

const ReportMedia = () => {
    const {auth} = useAuth();
    const [state, setState] = useState({loading: true, error: null, data: null});
    const [onlyOrphans, setOnlyOrphans] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const authHeader = {Authorization: `Bearer ${auth.token}`};

    const load = useCallback(async (orphans) => {
        setState((s) => ({...s, loading: true, error: null}));
        try {
            const {data} = await axios.get(`${API_ENDPOINT}/v1/admin/media`, {
                headers: {Authorization: `Bearer ${auth.token}`},
                params: {limit: 50, ...(orphans ? {orphans: 1} : {})},
            });
            setState({loading: false, error: null, data});
        } catch (e) {
            const msg = e?.response?.status === 403
                ? 'Nu ai drepturi pentru acest raport.'
                : 'Nu am putut incarca lista de imagini.';
            setState({loading: false, error: msg, data: null});
            NotificationManager.error(msg);
        }
    }, [auth.token]);

    useEffect(() => {
        load(onlyOrphans);
    }, [load, onlyOrphans]);

    const remove = async (item) => {
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(`Sterg definitiv "${item.name}"? Fisierul dispare de pe disc.`)) {
            return;
        }
        setDeleting(item.id);
        try {
            await axios.delete(`${API_ENDPOINT}/v1/admin/media/${item.id}`, {headers: authHeader});
            NotificationManager.success('Imagine stearsa.');
            load(onlyOrphans);
        } catch (e) {
            // 409 = imaginea aparține unei companii; serverul refuza, nu doar interfata
            NotificationManager.error(
                e?.response?.data?.error || 'Nu am putut sterge imaginea.'
            );
        } finally {
            setDeleting(null);
        }
    };

    if (state.loading) {
        return <div className="card"><div className="card-body text-center text-muted">Se incarca…</div></div>;
    }

    if (state.error) {
        return (
            <div className="card">
                <div className="card-body">
                    <p className="text-danger mb-2" style={{wordBreak: 'break-word'}}>{state.error}</p>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => load(onlyOrphans)}>Reincearca</button>
                </div>
            </div>
        );
    }

    const {items, total, orphanCount, orphanBytes} = state.data;

    return (
        <>
            {orphanCount > 0 && (
                <div className="callout callout-warning">
                    <span style={{wordBreak: 'break-word'}}>
                        <strong>{orphanCount}</strong> imagini orfane (fara companie) ocupa{' '}
                        <strong>{formatBytes(orphanBytes)}</strong>. Nu apar in nicio aplicatie
                        si nu pot fi sterse din portalul firmelor.
                    </span>
                </div>
            )}

            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">{total} imagini</h3>
                    <div className="card-tools">
                        <div className="custom-control custom-switch">
                            <input type="checkbox" className="custom-control-input" id="only-orphans"
                                   checked={onlyOrphans} onChange={(e) => setOnlyOrphans(e.target.checked)}/>
                            <label className="custom-control-label" htmlFor="only-orphans">Doar orfane</label>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {items.length === 0 && <p className="text-muted mb-0">Nicio imagine.</p>}
                    <div className="row">
                        {items.map((item) => (
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3" key={item.id}>
                                <div className={`card mb-0 ${item.orphan ? 'card-warning card-outline' : ''}`}>
                                    <a href={mediaUrl(item.contentUrl)} target="_blank" rel="noreferrer">
                                        <img src={mediaUrl(item.thumbUrl) || mediaUrl(item.contentUrl)}
                                             alt={item.name}
                                             className="card-img-top"
                                             style={{height: 140, objectFit: 'cover', background: '#f4f4f4'}}
                                             onError={(e) => {
                                                 e.target.style.display = 'none';
                                             }}/>
                                    </a>
                                    <div className="card-body p-2" style={{fontSize: 12}}>
                                        <div style={{wordBreak: 'break-word', fontWeight: 600}}>{item.name}</div>
                                        <div className="text-muted">
                                            {item.orphan
                                                ? <span className="badge badge-warning">orfana</span>
                                                : <code>{item.companyCode}</code>}
                                            {' · '}{formatBytes(item.sizeBytes)}
                                            {item.width ? ` · ${item.width}×${item.height}` : ''}
                                        </div>
                                        <div className="text-muted">{formatDate(item.createdAt)}</div>
                                        {item.urlName && (
                                            <div className="text-muted" style={{wordBreak: 'break-word'}}>
                                                link: {item.urlName}
                                            </div>
                                        )}
                                        {item.orphan && (
                                            <button className="btn btn-xs btn-outline-danger mt-2"
                                                    disabled={deleting === item.id}
                                                    onClick={() => remove(item)}>
                                                {deleting === item.id ? 'Se sterge…' : 'Sterge'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReportMedia;
